import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Button, Checkbox } from '@opendate-app/setlist';
import FanSuggestInput from './FanSuggestInput';
import TotalsTable from './TotalsTable';
import BoxOfficeGiftCardPayment from './BoxOfficeGiftCardPayment';
import CustomFieldValueFields from './../../CustomFieldValues/components/CustomFieldValueFields';
import {canSubmitCustomFieldValue} from './../../CustomFieldValues/actions/customFieldValuesActionCreators';
import { checkoutAnswers } from '../../CustomFieldValues/perTicketAnswers';
import TicketAnswerBlocks from '../../CustomFieldValues/components/TicketAnswerBlocks';

var _ = require('lodash');

const CASH_ORDER_TYPE = "Paid with Cash";

// https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
function validateEmail(email) {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

const formatPhone = (phoneNumber) => {
  const digits = (phoneNumber || "").replace(/\D/g, "").slice(-10);
  if (digits.length !== 10) return "";

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const contactInfoErrors = (ticketReservation) => {
  const errors = {};
  const email = (ticketReservation.email || "").trim();

  if (!(ticketReservation.first_name || "").trim()) errors.first_name = "Enter a first name";
  if (!(ticketReservation.last_name || "").trim()) errors.last_name = "Enter a last name";
  if (!email) {
    errors.email = "Enter an email address";
  } else if (!validateEmail(email)) {
    errors.email = "Enter a valid email address";
  }

  return errors;
};

const CheckoutForm = ({
  team,
  csrfToken,
  confirm,
  ticketReservation,
  ticketReservationChanged,
  updateTicketReservation,
  rootUrl,
  ticketTypes,
  isPlacingOrder,
  isPlacingOrderChanged,
  addOns,
  deleteTicketReservation,
  customFieldValues: allCustomFieldValues,
  customFieldValueChanged,
  defaultCountry,
  orderType,
  giftCardsEnabled
}) => {
  const { orderAnswers, blocks: ticketBlocks, all: customFieldValues } = checkoutAnswers(allCustomFieldValues || [], ticketTypes);
  const [showErrors, setShowErrors] = useState(false);
  const errors = showErrors ? contactInfoErrors(ticketReservation) : {};

  const canCheckout = (ticketReservation, isPlacingOrder) => {
    return (
      Object.keys(ticketReservation).length > 0
        && ticketReservation.token
        && ticketReservation.token.length > 0
        && Object.keys(contactInfoErrors(ticketReservation)).length === 0
        && !isPlacingOrder
        && customFieldValues.every((cfv) => canSubmitCustomFieldValue(cfv, { requireCheckedCheckbox: true }))
    );
  }

  const fanSelected = (fan) => {
    ticketReservationChanged({
      ...ticketReservation,
      first_name: fan.first_name || ticketReservation.first_name || "",
      last_name: fan.last_name || ticketReservation.last_name || "",
      email: fan.email || ticketReservation.email || ""
    });

    const phone = formatPhone(fan.phone);
    const phoneField = customFieldValues.find((cfv) => cfv.type === "CustomFieldValues::Phone");
    if (phone && phoneField && !phoneField.value) {
      customFieldValueChanged({ ...phoneField, value: phone });
    }
  };

  const handleSubmit = async (event) => {
    isPlacingOrderChanged(true);

    var completeUrl = (
      rootUrl + "/teams/" + team.id + "/manual_ticket_reservations/" + ticketReservation.token + "/complete"
    );

    const trimmed = {
      ...ticketReservation,
      first_name: ticketReservation.first_name.trim(),
      last_name: ticketReservation.last_name.trim(),
      email: ticketReservation.email.trim()
    };

    updateTicketReservation(csrfToken, team, trimmed, (ticketReservation) => {
      window.location.replace(completeUrl);
    }, customFieldValues);
  };

  return (
    <form noValidate
          onSubmit={
            (e) => {
              e.preventDefault();

              if(!canCheckout(ticketReservation, isPlacingOrder)){
                setShowErrors(true);
                return false;
              }

              handleSubmit(e);
            }
          }>
      <div className="row">
        <div className="col-12">
          <p style={{
               "fontSize": "16px",
               "marginBottom": "12px"
             }}>
            <strong>Contact Info</strong>
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="form-row mb-3">
            <div className="col">
              <FanSuggestInput team={team}
                               size="sm"
                               type="text"
                               aria-label="First name"
                               placeholder="First name"
                               error={errors.first_name}
                               onSelect={fanSelected}
                               value={ticketReservation.first_name || ""}
                               onChange={(e) => ticketReservationChanged({ ...ticketReservation, first_name: e.target.value })} />
            </div>
            <div className="col">
              <FanSuggestInput team={team}
                               size="sm"
                               type="text"
                               aria-label="Last name"
                               placeholder="Last name"
                               error={errors.last_name}
                               onSelect={fanSelected}
                               value={ticketReservation.last_name || ""}
                               onChange={(e) => ticketReservationChanged({ ...ticketReservation, last_name: e.target.value })} />
            </div>
          </div>
          <div className="form-row">
            <div className="col-12">
              <FanSuggestInput team={team}
                               size="sm"
                               type="email"
                               aria-label="Email address"
                               placeholder="Email address"
                               error={errors.email || ticketReservation.confirmEmailDoesNotMatch || undefined}
                               onSelect={fanSelected}
                               value={ticketReservation.email || ""}
                               onChange={(e) => ticketReservationChanged({ ...ticketReservation, email: e.target.value })} />
            </div>
          </div>
          <div className="form-row mt-3">
            <CustomFieldValueFields team={team}
                                csrfToken={csrfToken}
                                customFieldValues={orderAnswers.filter((cfv) => cfv.type === "CustomFieldValues::Phone")}
                                customFieldValueChanged={customFieldValueChanged}
                                highlightInvalidInputs={showErrors}
                                confirm={confirm} />
          </div>
        </div>
      </div>
      <CustomFieldValueFields team={team}
                              csrfToken={csrfToken}
                              customFieldValues={orderAnswers.filter((cfv) => cfv.type !== "CustomFieldValues::Phone")}
                              customFieldValueChanged={customFieldValueChanged}
                              confirm={confirm}
                              highlightInvalidInputs={showErrors}
                              defaultCountry={defaultCountry} />
      <TicketAnswerBlocks blocks={ticketBlocks}
                          team={team}
                          csrfToken={csrfToken}
                          customFieldValueChanged={customFieldValueChanged}
                          confirm={confirm}
                          highlightInvalidInputs={showErrors}
                          defaultCountry={defaultCountry} />
      <div className="row"
           style={{"marginTop": "20px"}}>
        <div className="col-12">
          <Checkbox id="order-subscribe-to-venue"
                    size="sm"
                    label="Send a confirmation email"
                    checked={ticketReservation.should_send_ticket_email || false}
                    onChange={(e) => ticketReservationChanged({ ...ticketReservation, should_send_ticket_email: e.target.checked })} />
        </div>
      </div>
      {giftCardsEnabled && orderType === CASH_ORDER_TYPE ? (
        <BoxOfficeGiftCardPayment team={team}
                                  csrfToken={csrfToken}
                                  ticketReservation={ticketReservation}
                                  ticketReservationChanged={ticketReservationChanged} />
      ) : null}
      <div className="row" style={{"marginTop": "30px"}}>
        <div className="col d-lg-none"
             style={{"marginBottom": "15px"}}>
          <TotalsTable ticketTypes={ticketTypes}
                       ticketReservation={ticketReservation}
                       addOns={addOns} />
        </div>
      </div>
      <div className="row"
           style={{"paddingTop": "5px"}}>
        <div className="col text-right">
          {showErrors && !customFieldValues.every((cfv) => canSubmitCustomFieldValue(cfv, { requireCheckedCheckbox: true })) ? (
            <p className="mb-2" style={{"color": "var(--sl-text-error)", "fontSize": "14px"}}>
              Complete the required fields above
            </p>
          ) : null}
          {!isPlacingOrder ? (
            <Button variant="link-gray"
                    className="mr-3"
                    onClick={() => deleteTicketReservation(csrfToken, team, ticketReservation)}>
              Cancel
            </Button>
          ) : null}
          <Button variant="primary"
                  type="submit"
                  disabled={isPlacingOrder}>
            {isPlacingOrder ? "Placing Order…" : "Place Order"}
          </Button>
        </div>
      </div>
    </form>
  );
};

CheckoutForm.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketReservation: PropTypes.object,
  ticketReservationChanged: PropTypes.func.isRequired,
  updateTicketReservation: PropTypes.func.isRequired,
  rootUrl: PropTypes.string.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  isPlacingOrder: PropTypes.bool,
  isPlacingOrderChanged: PropTypes.func.isRequired,
  addOns: PropTypes.array,
  deleteTicketReservation: PropTypes.func.isRequired,
  customFieldValues: PropTypes.array,
  customFieldValueChanged: PropTypes.func.isRequired,
  defaultCountry: PropTypes.string,
  orderType: PropTypes.string,
  giftCardsEnabled: PropTypes.bool
};

export default CheckoutForm;
